/**
 * Font size for the label's Print area box (Phase 104, D-09), shared by the
 * printed label (GarmentLabel, inline styles) and the layout editor's canvas
 * preview (ComponentRenderer), so both size it the same way.
 *
 * A print area line ("FRONT LEFT CHEST") must never be cut short: the printer
 * reads it to know where the print goes. So the text shrinks until the
 * longest line fits the box's width and every line fits its height, down to
 * `min`. Past `min` the renderers let the line wrap (never an ellipsis).
 *
 * The width estimate needs no measuring: the lines are upper case English in
 * a bold sans, whose average glyph is about 0.68 em wide.
 */

/** Average advance of a bold upper case Latin glyph, in em. */
const BOLD_CAPS_EM = 0.68;

export interface PrintAreaFitInput {
  lines: readonly string[];
  /** The box, in px. */
  width: number;
  height: number;
  /** The label's normal size: never larger than this. */
  base: number;
  /** The smallest that still prints legibly. */
  min: number;
  /** Left + right padding and border, px. */
  insetX?: number;
  lineHeight?: number;
}

export function printAreaFontSize({
  lines,
  width,
  height,
  base,
  min,
  insetX = 10,
  lineHeight = 1.15,
}: PrintAreaFitInput): number {
  if (lines.length === 0) return base;
  const longest = Math.max(1, ...lines.map((line) => line.length));
  const byHeight = height / (lines.length * lineHeight);
  const byWidth = Math.max(0, width - insetX) / (longest * BOLD_CAPS_EM);
  const size = Math.min(base, byHeight, byWidth);
  // Half-pixel steps keep the preview and the print identical.
  return Math.max(min, Math.floor(size * 2) / 2);
}
